import type { MovieFilters } from "@/utils/movie";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";

interface Option {
  label: string;
  value: string;
}

const yearOptions: Option[] = [
  { label: "全部年份", value: "" },
  { label: "2026", value: "2026-2026" },
  { label: "2025", value: "2025-2025" },
  { label: "2024 及更早", value: "0-2024" },
];

const ratingOptions: Option[] = [
  { label: "全部评分", value: "" },
  { label: "9.0 以上", value: "9-10" },
  { label: "8.0 以上", value: "8-10" },
  { label: "7.0 以上", value: "7-10" },
];

const regionOptions: Option[] = [
  { label: "全部地区", value: "all" },
  { label: "中国大陆", value: "中国大陆" },
  { label: "国际合作", value: "国际合作" },
];

const sortOptions: Option[] = [
  { label: "按热度", value: "heat" },
  { label: "按最新", value: "new" },
  { label: "按评分", value: "rating" },
];

const tagOptions: Option[] = Array.from(new Set(movies.flatMap((movie) => movie.tags))).slice(0, 14).map((tag) => ({
  label: tag,
  value: tag,
}));

interface FilterBarProps {
  filters: MovieFilters;
  onChange: (next: MovieFilters) => void;
  showGenre?: boolean;
  showRegion?: boolean;
  showTag?: boolean;
  showKeyword?: boolean;
}

function ChipRow({
  label,
  options,
  activeValue,
  onPick,
}: {
  label: string;
  options: Option[];
  activeValue: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/50 py-3 last:border-b-0 sm:flex-row sm:items-center">
      <span className="w-16 flex-shrink-0 font-label text-xs uppercase tracking-wide text-foreground-500">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.value === activeValue;
          return (
            <button
              key={option.value || "all"}
              type="button"
              onClick={() => onPick(option.value)}
              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                active
                  ? "bg-primary-500 text-foreground-950"
                  : "bg-background-100 text-foreground-300 hover:bg-background-200/70 hover:text-foreground-50"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  filters,
  onChange,
  showGenre = false,
  showRegion = false,
  showTag = false,
  showKeyword = true,
}: FilterBarProps) {
  const patch = (next: Partial<MovieFilters>) => onChange({ ...filters, ...next });

  const genreOptions: Option[] = [
    { label: "全部类型", value: "" },
    ...categories.map((category) => ({ label: category.name, value: category.key })),
  ];

  const resetAll = () =>
    onChange({ sort: filters.sort, keyword: showKeyword ? "" : filters.keyword });

  return (
    <div className="rounded-lg border border-background-200/60 bg-background-100/60 px-4 py-1 md:px-5">
      {showGenre && (
        <ChipRow
          label="类型"
          options={genreOptions}
          activeValue={filters.genre ?? ""}
          onPick={(value) => patch({ genre: value || undefined })}
        />
      )}
      <ChipRow
        label="年份"
        options={yearOptions}
        activeValue={filters.yearRange ?? ""}
        onPick={(value) => patch({ yearRange: value || undefined })}
      />
      <ChipRow
        label="评分"
        options={ratingOptions}
        activeValue={filters.ratingRange ?? ""}
        onPick={(value) => patch({ ratingRange: value || undefined })}
      />
      {showRegion && (
        <ChipRow
          label="地区"
          options={regionOptions}
          activeValue={filters.region ?? "all"}
          onPick={(value) => patch({ region: value })}
        />
      )}
      {showTag && (
        <ChipRow
          label="标签"
          options={[{ label: "全部标签", value: "" }, ...tagOptions]}
          activeValue={filters.keyword ?? ""}
          onPick={(value) => patch({ keyword: value })}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 py-3">
        <div className="flex items-center gap-2">
          <span className="font-label text-xs uppercase tracking-wide text-foreground-500">排序</span>
          <div className="flex gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((option) => {
              const active = (filters.sort ?? "heat") === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => patch({ sort: option.value })}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${
                    active ? "bg-primary-500 text-foreground-950" : "text-foreground-300 hover:text-foreground-50"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={resetAll}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-primary-400 hover:text-primary-400"
        >
          <i className="ri-refresh-line text-sm" />
          重置筛选
        </button>
      </div>
    </div>
  );
}